import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EyeIcon, PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTableQuery } from '../hooks/useTableQuery';
import { useAsideContext } from '@contexts/AsideContext';
import { PageMeta, TableCard, ColumnDefinition } from '@common/components';
import { Button, Badge, Input, Select, Modal } from '@common/components/ui';
import orderService from '@services/orderService';
import { Order, OrderStatus, PaymentStatus, FulfillmentStatus, Address, FinplusSale } from '@services/core/types';
import { ORDER_STATUS_BADGE } from '@/config/orderStatus';
import toast from 'react-hot-toast';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

const getStatusBadge = (status: OrderStatus) => {
  const config = ORDER_STATUS_BADGE[status] || { color: 'gray' as const, label: status };
  return <Badge color={config.color}>{config.label}</Badge>;
};

const getPaymentBadge = (status: PaymentStatus) => {
  const statusMap: Record<PaymentStatus, { color: 'success' | 'warning' | 'error' | 'gray'; label: string }> = {
    PENDING: { color: 'warning', label: 'Pending' },
    PAID: { color: 'success', label: 'Paid' },
    FAILED: { color: 'error', label: 'Failed' },
    REFUNDED: { color: 'gray', label: 'Refunded' },
    PARTIALLY_REFUNDED: { color: 'warning', label: 'Partial Refund' },
  };
  const config = statusMap[status] || { color: 'gray' as const, label: status };
  return <Badge color={config.color}>{config.label}</Badge>;
};

const formatCurrency = (amount: number, currency = 'EUR') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const Orders = () => {
  const queryClient = useQueryClient();
  const { openAside } = useAsideContext();

  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('');
  const [paymentFilter, setPaymentFilter] = useState<PaymentStatus | ''>('');
  const [fulfillmentFilter, setFulfillmentFilter] = useState<FulfillmentStatus | ''>('');

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<Array<{ src: string; alt: string }>>([]);

  // Fetch orders
  const { data: ordersData, isLoading, error } = useTableQuery({
    queryKey: ['orders', currentPage, pageSize, searchQuery, statusFilter, paymentFilter, fulfillmentFilter],
    fetchFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('limit', String(pageSize));
      params.set('sortBy', 'createdAt');
      params.set('sortDirection', 'desc');
      if (searchQuery) params.set('q', searchQuery);
      if (statusFilter) params.set('status', statusFilter);
      if (paymentFilter) params.set('paymentStatus', paymentFilter);
      if (fulfillmentFilter) params.set('fulfillmentStatus', fulfillmentFilter);
      return orderService.getAll(params);
    },
    options: {
      staleTime: 0,
      gcTime: 0,
      refetchInterval: 60 * 1000,
      refetchOnWindowFocus: true,
    },
  });

  // Order-specific mutations — these are domain actions (status transitions, processing,
  // rejection, address edits), not generic create/update/remove, so they stay as their
  // own mutations rather than being forced through useCrud.
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, paymentStatus, fulfillmentStatus }: {
      id: string;
      status: OrderStatus;
      paymentStatus?: PaymentStatus;
      fulfillmentStatus?: FulfillmentStatus;
    }) => orderService.updateStatus(id, status, paymentStatus, fulfillmentStatus),
    onSuccess: async (response) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (response.data) refreshOrderAside(response.data);
      toast.success('Order status updated successfully');
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to update order status'),
  });

  const processOrderMutation = useMutation({
    mutationFn: (orderId: string) => orderService.processOrder(orderId),
    onSuccess: async (response) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (response.data.order) refreshOrderAside(response.data.order);
      if (response.data.documentUrl) {
        window.open(response.data.documentUrl, '_blank');
      } else if (response.data.document) {
        const url = URL.createObjectURL(response.data.document);
        window.open(url, '_blank');
      }
      toast.success('Order processed successfully');
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to process order'),
  });

  const rejectOrderMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => orderService.rejectOrder(id, reason),
    onSuccess: async (response) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (response.data) refreshOrderAside(response.data);
      toast.success('Order rejected');
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to reject order'),
  });

  const updateAddressMutation = useMutation({
    mutationFn: ({ id, shippingAddress }: { id: string; shippingAddress: Address }) =>
      orderService.updateShippingAddress(id, shippingAddress),
    onSuccess: async (response) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (response.data) refreshOrderAside(response.data);
      toast.success('Shipping address updated successfully');
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to update shipping address'),
  });

  const markNotPickedUpMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => orderService.markNotPickedUp(id, reason),
    onSuccess: async (response) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (response.data) refreshOrderAside(response.data);
      toast.success('Order marked as not picked up');
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to update order'),
  });

  const createSaleMutation = useMutation({
    mutationFn: ({ id, finplusUserId }: { id: string; finplusUserId: number }) =>
      orderService.createFinplusSale(id, finplusUserId),
    onSuccess: async (response) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (response.data?.order) refreshOrderAside(response.data.order);
      toast.success(`Sale created in FinPlus (#${response.data?.sale.saleId})`);
      const warnings = response.data?.sale.priceWarnings?.length || 0;
      if (warnings > 0) {
        toast(`${warnings} price mismatch${warnings > 1 ? 'es' : ''} flagged by FinPlus`, { icon: '⚠️' });
      }
    },
    onError: (error: any) => {
      const errors = error.response?.data?.errors;
      const message = error.response?.data?.message || 'Failed to create sale in FinPlus';
      toast.error(Array.isArray(errors) && errors.length ? `${message}: ${errors.join(', ')}` : message);
    },
  });

  // Re-render the open Aside with the freshly-mutated order so status/details stay in sync.
  // `content` in AsideContext is a plain ReactNode snapshot, not a live subscription, so whatever
  // `isUpdating` we pass here is frozen until the next openAside call. Callers invoke this from a
  // mutation's onSuccess, where that specific action has just finished — and the UI disables all
  // actions while one is pending, so no other mutation can be concurrently in flight — so it's
  // always correct to pass `false` here rather than the (possibly still-stale-true) outer isUpdating.
  const refreshOrderAside = (order: Order) => {
    openAside(
      <OrderDetails
        order={order}
        onStatusChange={(newStatus) => handleStatusChange(order, newStatus)}
        onProcessOrder={() => processOrderMutation.mutate(order.id)}
        onRejectOrder={(reason) => rejectOrderMutation.mutate({ id: order.id, reason })}
        onUpdateAddress={(address) => updateAddressMutation.mutate({ id: order.id, shippingAddress: address })}
        onMarkNotPickedUp={(reason) => markNotPickedUpMutation.mutate({ id: order.id, reason })}
        onCreateSale={(finplusUserId) => createSaleMutation.mutateAsync({ id: order.id, finplusUserId })}
        isUpdating={false}
        onImageClick={(images) => {
          setLightboxImages(images);
          setLightboxOpen(true);
        }}
      />,
      { title: `Order #${order.orderNumber}` }
    );
  };

  const handleStatusChange = (order: Order, newStatus: OrderStatus) => {
    updateStatusMutation.mutate({
      id: order.id,
      status: newStatus,
      paymentStatus: order.paymentStatus,
      fulfillmentStatus: order.fulfillmentStatus,
    });
  };

  const handleViewOrder = (order: Order) => refreshOrderAside(order);

  const orders = ordersData?.data.orders || [];
  const total = ordersData?.data.total || 0;
  const totalPages = ordersData?.data.totalPages || 1;

  const clearFilters = () => {
    setStatusFilter('');
    setPaymentFilter('');
    setFulfillmentFilter('');
    setCurrentPage(1);
  };

  const hasFilters = statusFilter || paymentFilter || fulfillmentFilter;

  const columns: ColumnDefinition<Order>[] = [
    { name: 'orderNumber', title: 'Order #', value: (order) => <span className="font-medium text-gray-900 dark:text-white">#{order.orderNumber}</span> },
    {
      name: 'products',
      title: 'Products',
      value: (order) => (
        <div className="flex -space-x-2">
          {order.items?.slice(0, 3).map((item, index) =>
            item.image ? (
              <div
                key={item.id}
                className="h-10 w-10 rounded-lg overflow-hidden bg-gray-100 border-2 border-white dark:border-gray-800 cursor-pointer hover:scale-110 transition-transform"
                onClick={(e) => {
                  e.stopPropagation();
                  const images = order.items.filter((i) => i.image).map((i) => ({ src: i.image?.url || i.image, alt: i.title }));
                  setLightboxImages(images);
                  setLightboxOpen(true);
                }}
                style={{ zIndex: 3 - index }}
              >
                <img src={item.image?.url || item.image} alt={item.title} className="h-full w-full object-cover" />
              </div>
            ) : null
          )}
          {order.items && order.items.length > 3 && (
            <div className="h-10 w-10 rounded-lg bg-gray-200 dark:bg-gray-700 border-2 border-white dark:border-gray-800 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300">
              +{order.items.length - 3}
            </div>
          )}
        </div>
      ),
    },
    {
      name: 'customer',
      title: 'Customer',
      value: (order) => (
        <div>
          <div className="text-sm text-gray-900 dark:text-white">
            {order.shippingAddress?.firstName} {order.shippingAddress?.lastName}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{order.customerEmail || order.customerPhone}</div>
        </div>
      ),
    },
    { name: 'date', title: 'Date', value: (order) => formatDate(order.createdAt) },
    { name: 'total', title: 'Total', value: (order) => <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(order.total)}</span> },
    { name: 'payment', title: 'Payment', value: (order) => getPaymentBadge(order.paymentStatus) },
    {
      name: 'deliveryMethod',
      title: 'Method',
      value: (order) => (
        <Badge color={order.deliveryMethod === 'PICKUP' ? 'info' : 'gray'}>
          {order.deliveryMethod === 'PICKUP' ? 'Pickup' : 'Shipping'}
        </Badge>
      ),
    },
    { name: 'status', title: 'Status', value: (order) => getStatusBadge(order.status) },
  ];

  return (
    <>
      <PageMeta title="Orders" breadcrumbs={[{ label: 'Orders' }]} />

      <TableCard
        columns={columns}
        data={orders}
        loading={isLoading}
        error={error ? 'Failed to load orders' : undefined}
        emptyMessage="No orders found"
        searchable
        searchPlaceholder="Search by order number, email, or phone..."
        onSearch={(query) => {
          setSearchQuery(query);
          setCurrentPage(1);
        }}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as OrderStatus | '');
                setCurrentPage(1);
              }}
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'PENDING', label: 'Pending' },
                { value: 'CONFIRMED', label: 'Confirmed' },
                { value: 'PROCESSING', label: 'Processing' },
                { value: 'SHIPPED', label: 'Shipped' },
                { value: 'DELIVERED', label: 'Delivered' },
                { value: 'CANCELLED', label: 'Cancelled' },
                { value: 'READY_FOR_PICKUP', label: 'Ready for Pickup' },
                { value: 'FINISHED', label: 'Finished' },
                { value: 'NOT_PICKED_UP', label: 'Not Picked Up' },
              ]}
              fullWidth={false}
              className="w-40"
            />
            <Select
              value={paymentFilter}
              onChange={(e) => {
                setPaymentFilter(e.target.value as PaymentStatus | '');
                setCurrentPage(1);
              }}
              options={[
                { value: '', label: 'All Payments' },
                { value: 'PENDING', label: 'Pending' },
                { value: 'PAID', label: 'Paid' },
                { value: 'FAILED', label: 'Failed' },
              ]}
              fullWidth={false}
              className="w-36"
            />
            <Select
              value={fulfillmentFilter}
              onChange={(e) => {
                setFulfillmentFilter(e.target.value as FulfillmentStatus | '');
                setCurrentPage(1);
              }}
              options={[
                { value: '', label: 'All Fulfillment' },
                { value: 'UNFULFILLED', label: 'Unfulfilled' },
                { value: 'PARTIALLY_FULFILLED', label: 'Partially Fulfilled' },
                { value: 'FULFILLED', label: 'Fulfilled' },
              ]}
              fullWidth={false}
              className="w-44"
            />
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear
              </Button>
            )}
          </div>
        }
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onRowClick={handleViewOrder}
        rowActions={(order) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewOrder(order);
            }}
            className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
        )}
      />

      {/* Image Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => {
          setLightboxOpen(false);
          setLightboxImages([]);
        }}
        slides={lightboxImages}
      />
    </>
  );
};

interface OrderDetailsProps {
  order: Order;
  onStatusChange: (status: OrderStatus) => void;
  onProcessOrder: () => void;
  onRejectOrder: (reason: string) => void;
  onUpdateAddress: (address: Address) => void;
  onMarkNotPickedUp: (reason: string) => void;
  onCreateSale: (finplusUserId: number) => Promise<unknown>;
  isUpdating: boolean;
  onImageClick: (images: Array<{ src: string; alt: string }>) => void;
}

const OrderDetails = ({
  order,
  onStatusChange,
  onProcessOrder,
  onRejectOrder,
  onUpdateAddress,
  onMarkNotPickedUp,
  onCreateSale,
  isUpdating,
  onImageClick,
}: OrderDetailsProps) => {
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editedAddress, setEditedAddress] = useState<Address>(order.shippingAddress);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [notPickedUpReason, setNotPickedUpReason] = useState('');
  const [showNotPickedUpModal, setShowNotPickedUpModal] = useState(false);
  const [selectedFinplusUserId, setSelectedFinplusUserId] = useState('');
  const [isSubmittingSale, setIsSubmittingSale] = useState(false);

  const { data: finplusUsersData, isLoading: isLoadingFinplusUsers } = useQuery({
    queryKey: ['finplus-users'],
    queryFn: () => orderService.getFinplusUsers(),
    staleTime: 5 * 60 * 1000,
  });
  const finplusUsers = finplusUsersData?.data || [];
  const finplusSale = order.metadata?.finplusSale as FinplusSale | undefined;

  const handleCreateSale = async () => {
    if (!selectedFinplusUserId) return;
    setIsSubmittingSale(true);
    try {
      await onCreateSale(Number(selectedFinplusUserId));
    } catch {
      // error toast already shown by the mutation's onError
    } finally {
      setIsSubmittingSale(false);
    }
  };

  useEffect(() => {
    setEditedAddress(order.shippingAddress);
  }, [order.shippingAddress]);

  const formatOrderCurrency = (amount: number) => formatCurrency(amount, order.currency || 'EUR');

  const handleSaveAddress = () => {
    onUpdateAddress(editedAddress);
    setIsEditingAddress(false);
  };

  const handleCancelEditAddress = () => {
    setEditedAddress(order.shippingAddress);
    setIsEditingAddress(false);
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    onRejectOrder(rejectReason);
    setShowRejectModal(false);
    setRejectReason('');
  };

  const handleMarkNotPickedUp = () => {
    if (!notPickedUpReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    onMarkNotPickedUp(notPickedUpReason);
    setShowNotPickedUpModal(false);
    setNotPickedUpReason('');
  };

  // Determine which actions are available based on status and delivery method
  const isPickup = order.deliveryMethod === 'PICKUP';
  const canConfirm = order.status === 'PENDING';
  const canProcess = order.status === 'CONFIRMED';
  const canShip = !isPickup && order.status === 'PROCESSING';
  const canDeliver = !isPickup && order.status === 'SHIPPED';
  const canMarkReady = isPickup && order.status === 'PROCESSING';
  const canFinish = isPickup && order.status === 'READY_FOR_PICKUP';

  return (
    <div className="space-y-6">
      {/* Order Summary */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Order Summary</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
            <span className="font-medium text-gray-900 dark:text-white">{formatOrderCurrency(order.subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Tax:</span>
            <span className="font-medium text-gray-900 dark:text-white">{formatOrderCurrency(order.tax)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Shipping:</span>
            <span className="font-medium text-gray-900 dark:text-white">{formatOrderCurrency(order.shipping)}</span>
          </div>
          {order.discount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Discount:</span>
              <span>-{formatOrderCurrency(order.discount)}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
            <span className="font-medium text-gray-900 dark:text-white">Total:</span>
            <span className="font-bold text-gray-900 dark:text-white">{formatOrderCurrency(order.total)}</span>
          </div>
        </div>
      </div>

      {/* Customer Information */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Customer Information</h3>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          {order.customerEmail && (
            <div>
              <span className="font-medium text-gray-900 dark:text-white">Email: </span>
              {order.customerEmail}
            </div>
          )}
          {order.customerPhone && (
            <div>
              <span className="font-medium text-gray-900 dark:text-white">Phone: </span>
              {order.customerPhone}
            </div>
          )}
        </div>
      </div>

      {/* Shipping Address */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {isPickup ? 'Delivery' : 'Shipping Address'}
          </h3>
          {!isPickup && !isEditingAddress && (
            <button
              onClick={() => setIsEditingAddress(true)}
              className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {isPickup ? (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <p>
              {order.shippingAddress?.firstName} {order.shippingAddress?.lastName}
            </p>
            <p className="italic text-gray-500 dark:text-gray-500">In-store pickup — no shipping address required.</p>
          </div>
        ) : isEditingAddress ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input label="First Name" value={editedAddress.firstName} onChange={(e) => setEditedAddress({ ...editedAddress, firstName: e.target.value })} />
              <Input label="Last Name" value={editedAddress.lastName} onChange={(e) => setEditedAddress({ ...editedAddress, lastName: e.target.value })} />
            </div>
            <Input label="Address Line 1" value={editedAddress.address1} onChange={(e) => setEditedAddress({ ...editedAddress, address1: e.target.value })} />
            <Input
              label="Address Line 2 (Optional)"
              value={editedAddress.address2 || ''}
              onChange={(e) => setEditedAddress({ ...editedAddress, address2: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input label="City" value={editedAddress.city} onChange={(e) => setEditedAddress({ ...editedAddress, city: e.target.value })} />
              <Input label="ZIP Code" value={editedAddress.zip} onChange={(e) => setEditedAddress({ ...editedAddress, zip: e.target.value })} />
            </div>
            <Input label="Country" value={editedAddress.country} onChange={(e) => setEditedAddress({ ...editedAddress, country: e.target.value })} />
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSaveAddress} loading={isUpdating} size="sm" icon={<CheckIcon className="h-4 w-4" />} className="flex-1">
                Save
              </Button>
              <Button variant="outline" onClick={handleCancelEditAddress} disabled={isUpdating} size="sm" icon={<XMarkIcon className="h-4 w-4" />} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <p>
              {order.shippingAddress?.firstName} {order.shippingAddress?.lastName}
            </p>
            <p>{order.shippingAddress?.address1}</p>
            {order.shippingAddress?.address2 && <p>{order.shippingAddress.address2}</p>}
            <p>
              {order.shippingAddress?.city}, {order.shippingAddress?.zip}
            </p>
            <p>{order.shippingAddress?.country}</p>
          </div>
        )}
      </div>

      {/* Order Items */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Order Items</h3>
        <div className="space-y-3">
          {order.items?.map((item) => (
            <div key={item.id} className="flex gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              {item.image && (
                <img
                  src={item.image.url || item.image}
                  alt={item.title}
                  className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => {
                    const images = order.items.filter((i) => i.image).map((i) => ({ src: i.image?.url || i.image, alt: i.title }));
                    onImageClick(images);
                  }}
                />
              )}
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">{item.title}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  SKU: {item.sku} • Qty: {item.quantity}
                </div>
              </div>
              <div className="text-right">
                <div className="font-medium text-gray-900 dark:text-white">{formatOrderCurrency(item.totalPrice)}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{formatOrderCurrency(item.price)} each</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Status Management */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Order Actions</h3>
        {order.status === 'READY_FOR_PICKUP' && (
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-3 text-sm text-blue-800 dark:text-blue-300">
            Customer must collect this order within 24 hours of readiness. Remember to mark it manually if they don't show up.
          </div>
        )}
        <div className="space-y-2">
          {canConfirm && (
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={() => onStatusChange('CONFIRMED')} disabled={isUpdating}>
                Confirm Order
              </Button>
              <Button variant="danger" onClick={() => setShowRejectModal(true)} disabled={isUpdating}>
                Reject Order
              </Button>
            </div>
          )}

          {canProcess && (
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={onProcessOrder} disabled={isUpdating}>
                Process Order
              </Button>
              <Button variant="danger" onClick={() => setShowRejectModal(true)} disabled={isUpdating}>
                Reject Order
              </Button>
            </div>
          )}

          {canShip && (
            <Button onClick={() => onStatusChange('SHIPPED')} disabled={isUpdating} fullWidth>
              Mark as Shipped
            </Button>
          )}

          {canDeliver && (
            <Button onClick={() => onStatusChange('DELIVERED')} disabled={isUpdating} fullWidth>
              Mark as Delivered
            </Button>
          )}

          {canMarkReady && (
            <Button onClick={() => onStatusChange('READY_FOR_PICKUP')} disabled={isUpdating} fullWidth>
              Mark Ready for Pickup
            </Button>
          )}

          {canFinish && (
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={() => onStatusChange('FINISHED')} disabled={isUpdating}>
                Mark as Finished
              </Button>
              <Button variant="danger" onClick={() => setShowNotPickedUpModal(true)} disabled={isUpdating}>
                Mark as Not Picked Up
              </Button>
            </div>
          )}

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Current Status:</span>
              <span className="font-medium text-gray-900 dark:text-white">{order.status}</span>
            </div>
            <div className="flex justify-between items-center text-sm mt-2">
              <span className="text-gray-600 dark:text-gray-400">Payment Status:</span>
              <span className="font-medium text-gray-900 dark:text-white">{order.paymentStatus}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Create Sale (FinPlus) */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Create Sale</h3>
        {finplusSale ? (
          <div className="space-y-2 text-sm bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Badge color="success">Sold in FinPlus</Badge>
              <span className="text-gray-500 dark:text-gray-400">#{finplusSale.saleId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Total:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatOrderCurrency(finplusSale.total)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Created:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatDate(finplusSale.createdAt)}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Select
              value={selectedFinplusUserId}
              onChange={(e) => setSelectedFinplusUserId(e.target.value)}
              placeholder={isLoadingFinplusUsers ? 'Loading staff...' : 'Select staff member'}
              disabled={isLoadingFinplusUsers || isSubmittingSale}
              options={finplusUsers.map((user) => ({
                value: user.id,
                label: `${user.name} ${user.surname}`,
              }))}
            />
            <Button
              onClick={handleCreateSale}
              loading={isSubmittingSale}
              disabled={!selectedFinplusUserId || isLoadingFinplusUsers}
              fullWidth
            >
              Create Sale
            </Button>
          </div>
        )}
      </div>

      {/* Notes */}
      {order.notes && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Notes</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">{order.notes}</p>
        </div>
      )}

      {/* Reject Modal */}
      <Modal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} title="Reject Order" size="sm">
        <div className="space-y-4">
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Please provide a reason for rejection..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-700 dark:text-white resize-none"
            rows={4}
          />
          <div className="flex gap-2">
            <Button variant="danger" onClick={handleReject} disabled={isUpdating || !rejectReason.trim()} className="flex-1">
              Reject Order
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectModal(false);
                setRejectReason('');
              }}
              disabled={isUpdating}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Not Picked Up Modal */}
      <Modal isOpen={showNotPickedUpModal} onClose={() => setShowNotPickedUpModal(false)} title="Mark as Not Picked Up" size="sm">
        <div className="space-y-4">
          <textarea
            value={notPickedUpReason}
            onChange={(e) => setNotPickedUpReason(e.target.value)}
            placeholder="Please provide a reason (e.g. customer never came to collect)..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-700 dark:text-white resize-none"
            rows={4}
          />
          <div className="flex gap-2">
            <Button variant="danger" onClick={handleMarkNotPickedUp} disabled={isUpdating || !notPickedUpReason.trim()} className="flex-1">
              Mark as Not Picked Up
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowNotPickedUpModal(false);
                setNotPickedUpReason('');
              }}
              disabled={isUpdating}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Orders;
